import http from '@/http'
import router from '@/router'

import {
  TOC_RECEIVED,
  TOC_BOOK_RECIEVED,
  TOC_UPDATE_OPEN,
  TOC_UPDATE_SEARCH,
  TOC_LOADING,
  TOC_RESIZE,
  TOC_RESIZE_PANEL,
  TOC_SET_ACTIVE_CONTENT,
  TOC_SET_VIEWER_ACTIVE_CONTENT,
  TOC_SET_THREE_D_EXPANDED_MODELS,
  TOC_SET_THREE_D_SEARCH_CARRY_IN,
  TOC_SET_THREE_D_SEARCH_SELECTION,
  TOC_SET_THREE_D_SELECTION,
  TOC_THREE_D_SNAPSHOTS_RECEIVED,
  CONTENT_RECEIVED,
  BOM_ILLUSTRATION_RECEIVED,
  CLEAR_TOC
} from './contentTypes'
import { entityTypePluralize, routeToEntityTypeAndId } from './contentHelpers'
import { getRoute } from "@/router/getRoute"
import { getMediaThreeDModelSnapshots } from '@/controllers/library/media'
import { isNewAdminEnabled } from '@/composables/useShowNewAdmin'
import {
  getThreeDSnapshotSearchResult,
  hasMappedPartMatch
} from '@/helpers/threeDTocSearchCarryIn'
import {
  getThreeDTocSearchResultKey,
  isPageTocSearchResult,
  isThreeDModelTocSearchResult,
  isThreeDSnapshotTocSearchResult
} from '@/helpers/threeDTocSearch'

const countAssignedThreeDSearchResults = (snapshots, results) => {
  const assignedModelIds = new Set(snapshots.map(snapshot => String(snapshot.threeDModelId)))
  const assignedSnapshotIds = new Set(snapshots.map(snapshot => String(snapshot.id)))
  const keys = new Set()

  results.forEach((result) => {
    const entityId = String(result?.entityId ?? '')
    if (isThreeDModelTocSearchResult(result) && assignedModelIds.has(entityId)) {
      keys.add(`model:${entityId}`)
    }
    if (isThreeDSnapshotTocSearchResult(result) && assignedSnapshotIds.has(entityId)) {
      keys.add(`snapshot:${entityId}`)
    }
  })
  return keys.size
}

const state = getDefaultState()

const getters = {
  threeDModels: (state) => {
    const models = new Map()
    state.threeDModelSnapshots.forEach((snapshot) => {
      let model = models.get(snapshot.threeDModelId)
      if (!model) {
        model = {
          id: snapshot.threeDModelId,
          identifier: snapshot.threeDModelIdentifier,
          name: snapshot.threeDModelName || snapshot.threeDModelIdentifier,
          thumbnailUrl: snapshot.threeDModelThumbnailUrl || null,
          snapshots: []
        }
        models.set(snapshot.threeDModelId, model)
      }
      model.snapshots.push(snapshot)
    })
    return [...models.values()]
  },
  selectedThreeDModel: (state, getters) => {
    return getters.threeDModels.find(model => model.id === state.selectedThreeDModelId) || null
  },
  selectedThreeDSnapshot: (state) => {
    return state.threeDModelSnapshots.find(snapshot => snapshot.id === state.selectedThreeDSnapshotId) || null
  },
  threeDTocSearchResults: (state, getters) => {
    const resultsByKey = new Map()
    state.searchResults.forEach((result) => {
      const key = getThreeDTocSearchResultKey(result)
      if (key && !resultsByKey.has(key)) resultsByKey.set(key, result)
    })

    return getters.threeDModels.reduce((results, model) => {
      const modelResult = resultsByKey.get(`model:${String(model.id)}`)
      if (modelResult) results.push(modelResult)
      model.snapshots.forEach((snapshot) => {
        const snapshotResult = resultsByKey.get(`snapshot:${String(snapshot.id)}`)
        if (snapshotResult) results.push(snapshotResult)
      })
      return results
    }, [])
  },
  tocChildren: (state, getters, rootState) => {
    const item = state.itemsMap[`${rootState.content.type}:${rootState.content.id}`]
    return item ? item.children : state.items
  },
  tocSearchMatchedItemsList(state) {
    const results = Array.isArray(state.searchResults) ? state.searchResults : []
    const itemsList = Array.isArray(state.itemsList) ? state.itemsList : []
    if (!results.length || !itemsList.length) return []

    const keySet = new Set(
      results
        .filter(r => r?.entityType && r?.entityId != null)
        .map(r => `${String(r.entityType).trim().toLowerCase()}:${String(r.entityId)}`)
    )
    return itemsList.filter((path) => {
      if (!path) return false
      const parts = String(path).split('/')
      const last = parts[parts.length - 1] || ''
      return keySet.has(String(last).trim().toLowerCase())
    })
  },
  /*
   * state.items is a nested Array of item DTOs.
   * Each DTO contains metadata for chapters & pages in the book.
   *  e.g. { id: number, type: 'chapter' || 'page',
   *          children?: Array, ... }
   * Chapters may contain a 'children' field,
   * which is an embedded list of nested item DTOs.
   * This getter returns a flattened Map that uses as keys
   * the stringified route path in use in Library BOM TOC.
   * Each key's value is an object of the item DTO,
   * index position of the item in a flat list,
   * & a set of chapter Id showing how nested it is.
   * e.g "/chapter:71/page:1711" -> {
   *    chapters: Set[71]
   *    index: 0,
   *    item: ItemDto {}
   * }
   * It allows O(1) lookups of metadata with the item route path.
  */
  widgetTocFlatMap(state) {
    return state.items.reduce(recursiveReducer(), new Map())

    function recursiveReducer(prefix = '', previousChapters = []) {
      return function(flatMap, item) {
        const id = `${prefix}/${item.type}:${item.id}`
        const chapters = previousChapters
          .concat((item.type === 'chapter') ? item.id : [])

        flatMap.set(id,
          { item, index: flatMap.size, chapters: new Set(chapters) })

        if (!!item.children) {
          item.children.forEach(child => {
            recursiveReducer(id, chapters)(flatMap, child)
          })
        }
        return flatMap
      }
    }
  },
  /*
   * This list returns a flat list to be used, in contrast to
   * the above flat Map, for O(1) lookups when the index is known
   * rather than the parsed Item route path.
   */
  widgetTocFlatList(state, getters) {
    return [ ...getters.widgetTocFlatMap.values() ]
  }
}

let tocSearchRequestId = 0
let tocRequestId = 0
let threeDRequestId = 0
let threeDModelSnapshotsLoadPromise = null
let resolveThreeDModelSnapshotsLoad = null

const beginThreeDModelSnapshotsLoad = () => {
  if (resolveThreeDModelSnapshotsLoad) resolveThreeDModelSnapshotsLoad()
  threeDModelSnapshotsLoadPromise = new Promise(resolve => {
    resolveThreeDModelSnapshotsLoad = resolve
  })
}

const completeThreeDModelSnapshotsLoad = () => {
  if (resolveThreeDModelSnapshotsLoad) resolveThreeDModelSnapshotsLoad()
  resolveThreeDModelSnapshotsLoad = null
  threeDModelSnapshotsLoadPromise = null
}

const actions = {
  async getTOC ({ commit, rootState, state, dispatch }, { id, type }) {
    const requestId = ++tocRequestId
    try {
      const entities = routeToEntityTypeAndId(id, type)
      const { 0: first } = entities

      if (type === 'book') {
        const isNewBook = !state.book || id !== state.book.id
        if (isNewBook) {
          commit(TOC_LOADING)
          const tocDimensions = await dispatch('getLocalStorage', { key: 'toc' }, { root: true })
          if (requestId !== tocRequestId) return
          if (tocDimensions) {
            commit(TOC_RESIZE, tocDimensions)
          }
          const tocPanelWidth = localStorage.getItem('tocPanelWidth')
          if (tocPanelWidth != null) {
            commit(TOC_RESIZE_PANEL, { width: tocPanelWidth })
          }

          const entityTypePlural = entityTypePluralize(type)
          const fetch = [
            http.get(`${entityTypePlural}/${id}/table-of-contents`, {
              params: {
                'nest-children': true
              }
            })
          ]

          /*
           * This if-check and the one below it help ensure that
           * multiple GET requests for the same content
           * are not performed to "/media/:id"
           * Equivalent code to this block conditionally runs in the
           * getContent action found in content.js
          */
          fetch.push(http.get(`${entityTypePlural}/${id}`))
          const [result1, result2] = await Promise.all(fetch)
          if (requestId !== tocRequestId) return

          if (result2.data) {
            const content = result2.data
            content.bookId = entities.length > 1 ? first.id : ''
            commit(CONTENT_RECEIVED, { content })
            commit(BOM_ILLUSTRATION_RECEIVED, {
              illustration: rootState.content.contentType === 'illustration' ? content : null
            })
          }

          commit(TOC_RECEIVED, { toc: result1.data })
          commit(TOC_BOOK_RECIEVED, { book: result2 ? result2.data : rootState.content })

          const currentRoute = getRoute()
          if (rootState.content.id === id && currentRoute.name === 'Asset') {
            commit(TOC_UPDATE_OPEN, { isTocOpen: rootState.user.preferences.tocState })
          }

          commit(TOC_THREE_D_SNAPSHOTS_RECEIVED, {
            isLoadComplete: false,
            isNewBook: true,
            snapshots: []
          })
        }
        dispatch('loadThreeDModelSnapshots', {
          isNewBook,
          mediaId: first.id
        }).catch(() => {})
      } else {
        threeDRequestId++
        commit(TOC_RECEIVED, { toc: [] })
        commit(TOC_THREE_D_SNAPSHOTS_RECEIVED, { snapshots: [] })
        commit(TOC_BOOK_RECIEVED, { book: null })
      }
    } catch (err) {
      // na
    }
  },
  async loadThreeDModelSnapshots ({ commit, rootGetters, state }, { isNewBook, mediaId }) {
    const requestId = ++threeDRequestId
    if (rootGetters['widgets/isWidget'] || !isNewAdminEnabled()) {
      commit(TOC_THREE_D_SNAPSHOTS_RECEIVED, {
        isNewBook,
        snapshots: []
      })
      return
    }

    try {
      const snapshots = await getMediaThreeDModelSnapshots(mediaId)
      if (requestId !== threeDRequestId || String(state.book?.id) !== String(mediaId)) return

      commit(TOC_THREE_D_SNAPSHOTS_RECEIVED, {
        isNewBook,
        snapshots
      })
    } catch {
      if (requestId === threeDRequestId && String(state.book?.id) === String(mediaId)) {
        commit(TOC_THREE_D_SNAPSHOTS_RECEIVED, {
          isNewBook,
          snapshots: []
        })
      }
    }
  },
  async updateTocOpen ({ commit }, isOpen) {
    commit(TOC_UPDATE_OPEN, { isTocOpen: isOpen })
  },
  async updateTocActiveContent ({ commit, state }, activeContent) {
    commit(TOC_SET_ACTIVE_CONTENT, { activeContent })
    if (!String(state.query || '').trim()) {
      commit(TOC_SET_VIEWER_ACTIVE_CONTENT, { activeContent })
    }
  },
  async updateViewerActiveContent ({ commit }, activeContent) {
    commit(TOC_SET_VIEWER_ACTIVE_CONTENT, { activeContent })
    if (activeContent === '2d') {
      commit(TOC_SET_THREE_D_SEARCH_SELECTION, { key: '' })
    }
  },
  async selectThreeDModel ({ commit }, modelId) {
    commit(TOC_SET_VIEWER_ACTIVE_CONTENT, { activeContent: '3d' })
    commit(TOC_SET_THREE_D_SEARCH_SELECTION, { key: '' })
    commit(TOC_SET_THREE_D_SEARCH_CARRY_IN, { query: '' })
    commit(TOC_SET_THREE_D_SELECTION, { modelId, snapshotId: null })
  },
  async selectThreeDSnapshot ({ commit, state }, payload) {
    const hasSelectionContext = payload?.snapshot && typeof payload.snapshot === 'object'
    const snapshot = hasSelectionContext ? payload.snapshot : payload
    if (!snapshot) return

    const assignedSnapshot = state.threeDModelSnapshots.find(({ id }) => id === snapshot.id)
    if (!assignedSnapshot) return

    commit(TOC_SET_VIEWER_ACTIVE_CONTENT, { activeContent: '3d' })
    commit(TOC_SET_THREE_D_SEARCH_SELECTION, { key: '' })

    const query = hasSelectionContext && typeof payload.query === 'string'
      ? payload.query
      : ''
    const searchResult = hasSelectionContext && payload.searchResult
      ? getThreeDSnapshotSearchResult([payload.searchResult], assignedSnapshot.id)
      : null
    const carryInQuery = typeof query === 'string' && query.trim() && hasMappedPartMatch(searchResult)
      ? query
      : ''

    commit(TOC_SET_THREE_D_SEARCH_CARRY_IN, {
      query: carryInQuery,
      snapshotId: assignedSnapshot.id
    })

    if (state.selectedThreeDSnapshotId === snapshot.id) return

    commit(TOC_SET_THREE_D_SELECTION, {
      modelId: assignedSnapshot.threeDModelId,
      snapshotId: assignedSnapshot.id
    })
  },
  async selectThreeDTocSearchResult ({ commit, dispatch, getters, state }, { searchResult }) {
    const key = getThreeDTocSearchResultKey(searchResult)
    const assignedResult = getters.threeDTocSearchResults.find(result =>
      getThreeDTocSearchResultKey(result) === key
    )
    if (!assignedResult) return false

    if (isThreeDModelTocSearchResult(assignedResult)) {
      const model = getters.threeDModels.find(({ id }) =>
        String(id) === String(assignedResult.entityId)
      )
      if (!model) return false
      await dispatch('selectThreeDModel', model.id)
      commit(TOC_SET_THREE_D_SEARCH_SELECTION, { key })
      return true
    }

    const snapshot = state.threeDModelSnapshots.find(({ id }) =>
      String(id) === String(assignedResult.entityId)
    )
    if (!snapshot) return false

    await dispatch('selectThreeDSnapshot', {
      query: state.query,
      searchResult: assignedResult,
      snapshot
    })
    commit(TOC_SET_THREE_D_SEARCH_SELECTION, { key })
    return true
  },
  async toggleThreeDModelExpanded ({ commit, state }, modelId) {
    const expandedModelIds = new Set(state.expandedThreeDModelIds)
    if (expandedModelIds.has(modelId)) expandedModelIds.delete(modelId)
    else expandedModelIds.add(modelId)
    commit(TOC_SET_THREE_D_EXPANDED_MODELS, { modelIds: [...expandedModelIds] })
  },
  async setAllThreeDModelsExpanded ({ commit, getters }, expanded) {
    commit(TOC_SET_THREE_D_EXPANDED_MODELS, {
      modelIds: expanded ? getters.threeDModels.map(model => model.id) : []
    })
  },
  async tocSearchWithReturn ({ commit, state }, { query }) {
    const requestId = ++tocSearchRequestId
    try {
      const trimmedQuery = query.trim()
      if (trimmedQuery) {
        commit(TOC_UPDATE_SEARCH, {
          query: trimmedQuery,
          count: 0,
          items: [],
          threeDCount: 0,
          twoDCount: 0
        })
        if (!state.isThreeDModelSnapshotsLoaded && threeDModelSnapshotsLoadPromise) {
          await threeDModelSnapshotsLoadPromise
          if (requestId !== tocSearchRequestId) return []
        }
        const modelCount = new Set(
          state.threeDModelSnapshots.map(snapshot => snapshot.threeDModelId)
        ).size
        const length = Object.keys(state.itemsMap).length +
          modelCount + state.threeDModelSnapshots.length

        var newQuery
        if (!trimmedQuery.startsWith("\"")) {
          newQuery = '(' + trimmedQuery + ') OR (' + trimmedQuery + '*)'
        } else {
          newQuery = trimmedQuery
        }
        // offset not supported by the toc-search endpoint
        const params = {
          limit: length || 1000,
          fk_media_id: state.book.id,
          q: newQuery
        }

        const { data } = await http.get('toc-search', { params })
        if (requestId !== tocSearchRequestId) return []
        const items = data.items || []
        const pageIds = new Set(
          items
            .filter((it) => {
              return isPageTocSearchResult(it) && it?.entityId != null
            })
            .map((it) => String(it.entityId))
        )
        //filtered search counts need a single source of record now for more than one component
        //so we calculate the value here
        const countMatchingTocPages = (tocItems) => {
          return (tocItems || []).reduce((acc, item) => {
            const type = (item?.type == null ? '' : String(item.type)).trim().toLowerCase()
            const isPage = type === 'page'
            const id = (item?.id == null ? '' : String(item.id))
            const children = Array.isArray(item?.children) ? item.children : []
            return acc + ((isPage && pageIds.has(id)) ? 1 : 0) + countMatchingTocPages(children)
          }, 0)
        }

        const pageCount = countMatchingTocPages(state.items)
        const threeDCount = countAssignedThreeDSearchResults(state.threeDModelSnapshots, items)
        if (requestId !== tocSearchRequestId) return []
        commit(TOC_UPDATE_SEARCH, {
          query,
          count: pageCount + threeDCount,
          items,
          threeDCount,
          twoDCount: pageCount
        })
        const activeResultCount = state.activeContent === '2d' ? pageCount : threeDCount
        const otherResultCount = state.activeContent === '2d' ? threeDCount : pageCount
        if (activeResultCount === 0 && otherResultCount > 0) {
          commit(TOC_SET_ACTIVE_CONTENT, {
            activeContent: state.activeContent === '2d' ? '3d' : '2d'
          })
        }
        return data.items
      }
      if (requestId !== tocSearchRequestId) return []
      commit(TOC_UPDATE_SEARCH, {
        query: '',
        count: 0,
        items: [],
        threeDCount: 0,
        twoDCount: 0
      })
    } catch (err) {
      // na
      if (requestId !== tocSearchRequestId) return []
      if (query) {
        commit(TOC_UPDATE_SEARCH, {
          query,
          count: 0,
          items: [],
          threeDCount: 0,
          twoDCount: 0
        })
      }
    }
    return []
  },
  async tocResize ({ commit, dispatch }, { width, height }) {
    try {
      commit(TOC_RESIZE, { width, height })
      dispatch('setLocalStorage', {
        key: 'toc',
        value: {
          width,
          height
        }
      }, { root: true })
    } catch (err) {
      // na
    }
    return []
  },
  async tocResizePanel ({ commit, dispatch }, { width }) {
    try {
      commit(TOC_RESIZE_PANEL, { width })
      localStorage.setItem('tocPanelWidth', width)
    } catch (err) {
    }
    return []
  }
}

const mutations = {
  [TOC_RECEIVED] (state, { toc }) {
    tocSearchRequestId++
    state.items = toc
    state.isThreeDModelSnapshotsLoaded = false
    state.query = ''
    state.resultCount = 0
    state.threeDResultCount = 0
    state.twoDResultCount = 0
    state.searchResults = []
    state.selectedThreeDSearchResultKey = ''

    const flatten = (arr, result = {}) => {
      arr.forEach((item) => {
        result[`${item.type}:${item.id}`] = item; // eslint-disable-line
        if (item.children) {
          flatten(item.children, result)
        }
      })
      return result
    }
    state.itemsMap = flatten(toc)

    const listify = (arr, result = [], prefix = '') => {
      arr.forEach((item) => {
        const id = `${prefix}/${item.type}:${item.id}`
        if (item.type === 'page' || item.type === 'chapter') {
          result.push(id)
        }
        if (item.children) {
          listify(item.children, result, id)
        }
      })
      return result
    }
    state.itemsList = listify(toc)

    state.isLoaded = true
  },
  [TOC_BOOK_RECIEVED] (state, { book }) {
    state.book = book
      ? {
          id: book.id || '',
          identifier: book.identifier || '',
          name: book.name || '',
          type: book.type || '',
          contentType: book.contentType || '',
          disableExport: book.disableExport || false
        }
      : null
  },
  [TOC_UPDATE_SEARCH] (state, { query, count, items, threeDCount, twoDCount }) {
    const previousQuery = String(state.query || '').trim()
    const nextQuery = String(query || '').trim()
    const wasSearching = Boolean(previousQuery)
    state.query = query
    if (count != null) {
      state.resultCount = count
    }
    if (items != null) {
      state.searchResults = Array.isArray(items) ? items : []
    }
    if (threeDCount != null) {
      state.threeDResultCount = threeDCount
    }
    if (twoDCount != null) {
      state.twoDResultCount = twoDCount
    }
    if (!nextQuery || nextQuery !== previousQuery) {
      state.selectedThreeDSearchResultKey = ''
    }
    if (wasSearching && !nextQuery) {
      state.expandedThreeDModelIds = [
        ...new Set(state.threeDModelSnapshots.map(snapshot => snapshot.threeDModelId))
      ]
    }
  },
  [TOC_UPDATE_OPEN] (state, { isTocOpen }) {
    state.isTocOpen = isTocOpen
  },
  [TOC_LOADING] (state) {
    tocSearchRequestId++
    completeThreeDModelSnapshotsLoad()
    state.isLoaded = false
    state.threeDViewerSearchCarryIn = null
    state.threeDViewerSearchCarryInRequestId = 0
    state.isThreeDModelSnapshotsLoaded = false
    state.query = ''
    state.resultCount = 0
    state.searchResults = []
    state.selectedThreeDSearchResultKey = ''
    state.threeDResultCount = 0
    state.twoDResultCount = 0
  },
  [TOC_RESIZE] (state, { width, height }) {
    state.width = width
    state.height = height
  },
  [TOC_RESIZE_PANEL] (state, { width }) {
    state.panelWidth = width
  },
  [TOC_SET_ACTIVE_CONTENT] (state, { activeContent }) {
    if (activeContent === '2d' && state.items.length > 0) {
      state.activeContent = activeContent
    }
    if (activeContent === '3d' && state.threeDModelSnapshots.length > 0) {
      state.activeContent = activeContent
    }
  },
  [TOC_SET_VIEWER_ACTIVE_CONTENT] (state, { activeContent }) {
    if (activeContent === '2d') {
      state.viewerActiveContent = activeContent
      state.threeDViewerSearchCarryIn = null
    }
    if (activeContent === '3d' && state.threeDModelSnapshots.length > 0) {
      state.viewerActiveContent = activeContent
    }
  },
  [TOC_SET_THREE_D_EXPANDED_MODELS] (state, { modelIds }) {
    state.expandedThreeDModelIds = modelIds
  },
  [TOC_SET_THREE_D_SEARCH_CARRY_IN] (state, { query, snapshotId }) {
    if (!query || snapshotId == null) {
      state.threeDViewerSearchCarryIn = null
      return
    }

    state.threeDViewerSearchCarryInRequestId++
    state.threeDViewerSearchCarryIn = {
      query,
      requestId: state.threeDViewerSearchCarryInRequestId,
      snapshotId
    }
  },
  [TOC_SET_THREE_D_SEARCH_SELECTION] (state, { key }) {
    state.selectedThreeDSearchResultKey = key || ''
  },
  [TOC_SET_THREE_D_SELECTION] (state, { modelId, snapshotId }) {
    state.selectedThreeDModelId = modelId
    state.selectedThreeDSnapshotId = snapshotId
  },
  [TOC_THREE_D_SNAPSHOTS_RECEIVED] (state, {
    isLoadComplete = true,
    isNewBook,
    snapshots
  }) {
    if (isLoadComplete) completeThreeDModelSnapshotsLoad()
    else if (!threeDModelSnapshotsLoadPromise) beginThreeDModelSnapshotsLoad()
    const previousModelIds = new Set(state.threeDModelSnapshots.map(snapshot => snapshot.threeDModelId))
    state.threeDModelSnapshots = Array.isArray(snapshots) ? snapshots : []
    state.isThreeDModelSnapshotsLoaded = isLoadComplete
    const modelIds = [...new Set(state.threeDModelSnapshots.map(snapshot => snapshot.threeDModelId))]
    if (isNewBook) {
      state.expandedThreeDModelIds = modelIds
      state.selectedThreeDModelId = null
      state.selectedThreeDSnapshotId = null
      state.threeDViewerSearchCarryIn = null
      state.threeDViewerSearchCarryInRequestId = 0
      state.selectedThreeDSearchResultKey = ''
    } else {
      const retainedExpandedIds = state.expandedThreeDModelIds
        .filter(modelId => modelIds.includes(modelId))
      const newlyAssignedModelIds = modelIds.filter(modelId => !previousModelIds.has(modelId))
      state.expandedThreeDModelIds = [...new Set([...retainedExpandedIds, ...newlyAssignedModelIds])]

      if (!modelIds.includes(state.selectedThreeDModelId)) {
        state.selectedThreeDModelId = null
        state.selectedThreeDSearchResultKey = ''
        state.selectedThreeDSnapshotId = null
      } else if (!state.threeDModelSnapshots.some(snapshot => snapshot.id === state.selectedThreeDSnapshotId)) {
        state.selectedThreeDSearchResultKey = ''
        state.selectedThreeDSnapshotId = null
      }
    }
    const hasTwoDContent = state.items.length > 0
    const hasThreeDContent = state.threeDModelSnapshots.length > 0
    const isActiveContentAvailable = state.activeContent === '2d'
      ? hasTwoDContent
      : hasThreeDContent

    if (isNewBook || !isActiveContentAvailable) {
      state.activeContent = hasTwoDContent || !hasThreeDContent ? '2d' : '3d'
    }
    if (state.threeDViewerSearchCarryIn?.snapshotId !== state.selectedThreeDSnapshotId) {
      state.threeDViewerSearchCarryIn = null
    }
    const viewerActiveContent = state.viewerActiveContent || state.activeContent
    const isViewerContentAvailable = viewerActiveContent === '2d'
      ? hasTwoDContent
      : hasThreeDContent
    if (isNewBook || !isViewerContentAvailable) {
      state.viewerActiveContent = hasTwoDContent || !hasThreeDContent ? '2d' : '3d'
    }
    if (state.query) {
      state.threeDResultCount = countAssignedThreeDSearchResults(
        state.threeDModelSnapshots,
        state.searchResults
      )
      state.resultCount = state.twoDResultCount + state.threeDResultCount
      const activeResultCount = state.activeContent === '2d'
        ? state.twoDResultCount
        : state.threeDResultCount
      const otherResultCount = state.activeContent === '2d'
        ? state.threeDResultCount
        : state.twoDResultCount
      if (activeResultCount === 0 && otherResultCount > 0) {
        state.activeContent = state.activeContent === '2d' ? '3d' : '2d'
      }
    }
  },
  [CLEAR_TOC] (state) {
    tocSearchRequestId++
    completeThreeDModelSnapshotsLoad()
    Object.assign(state, getDefaultState())
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}

export function getDefaultState() {
  return {
    book: undefined,
    activeContent: '2d',
    viewerActiveContent: '2d',
    expandedThreeDModelIds: [],
    query: '',
    resultCount: 0,
    threeDResultCount: 0,
    searchResults: [],
    threeDModelSnapshots: [],
    selectedThreeDModelId: null,
    selectedThreeDSearchResultKey: '',
    selectedThreeDSnapshotId: null,
    threeDViewerSearchCarryIn: null,
    threeDViewerSearchCarryInRequestId: 0,
    items: [],
    itemsMap: {},
    itemsList: [],
    twoDResultCount: 0,
    isTocOpen: false,
    isLoaded: false,
    isThreeDModelSnapshotsLoaded: false,
    width: 500,
    height: 500,
    panelWidth: 400
  }
}
